import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '@/lib/api'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import { AtSign, Check, Copy, Eye, ExternalLink, Mail, Pause, Phone, Play, RefreshCw, Trash2, UserCheck, WalletCards, X } from 'lucide-react'

const money = (cents: number | string | null | undefined) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(Number(cents || 0) / 100)

const date = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '-'

interface ApplicationRow {
  id: string
  name: string
  email: string
  company: string | null
  website: string | null
  audience: string | null
  channel: string | null
  notes: string | null
  status: string
  createdAt: string
}

export default function AffiliatesPage() {
  const { toast } = useToast()
  const [applications, setApplications] = useState<ApplicationRow[]>([])
  const [affiliates, setAffiliates] = useState<any[]>([])
  const [commissions, setCommissions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [approveTarget, setApproveTarget] = useState<ApplicationRow | null>(null)
  const [approveForm, setApproveForm] = useState({
    commissionPct: 20,
    termMonths: 12,
    unlimited: false,
    internalNotes: '',
  })
  const [approving, setApproving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteAppTarget, setDeleteAppTarget] = useState<ApplicationRow | null>(null)
  const [deletingApp, setDeletingApp] = useState(false)
  const [aliasTarget, setAliasTarget] = useState<any | null>(null)
  const [aliasForm, setAliasForm] = useState({ localPart: '', displayName: '' })
  const [savingAlias, setSavingAlias] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [appsRes, affiliatesRes, commissionsRes] = await Promise.all([
        api.get('/api/affiliates/applications'),
        api.get('/api/affiliates'),
        api.get('/api/affiliates/commissions'),
      ])
      setApplications(appsRes.data.applications || [])
      setAffiliates(affiliatesRes.data.affiliates || [])
      setCommissions(commissionsRes.data.commissions || [])
    } catch (err: any) {
      toast({
        title: 'Failed to load affiliates',
        description: err?.response?.data?.message || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { load() }, [load])

  async function approveApplication() {
    if (!approveTarget || approving) return
    setApproving(true)
    try {
      const res = await api.post(`/api/affiliates/applications/${approveTarget.id}/approve`, approveForm)
      const idempotent = !!res.data?.idempotent
      toast({
        title: idempotent ? 'Already approved' : 'Affiliate approved',
        description: idempotent
          ? `${approveTarget.name} was already approved (no changes made).`
          : res.data.accessEmailSent
          ? `${approveTarget.name} received setup access, demo access, and an affiliate link.`
          : `${approveTarget.name} was approved, but the access email was not confirmed. Use Send access from the affiliate list.`,
      })
      setApproveTarget(null)
      await load()
    } catch (err: any) {
      toast({
        title: 'Approval failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setApproving(false)
    }
  }

  async function confirmDeleteApplication() {
    if (!deleteAppTarget) return
    setDeletingApp(true)
    try {
      await api.delete(`/api/affiliates/applications/${deleteAppTarget.id}`)
      toast({ title: 'Application deleted', description: `${deleteAppTarget.email} can re-apply.` })
      setDeleteAppTarget(null)
      await load()
    } catch (err: any) {
      toast({
        title: 'Delete failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setDeletingApp(false)
    }
  }

  async function setDealBuilderAccess(row: any, level: string) {
    try {
      await api.patch(`/api/affiliates/${row.id}`, { dealBuilderAccess: level })
      toast({
        title: 'Deal Builder access updated',
        description:
          level === 'none'
            ? `${row.display_name} can no longer build custom deals.`
            : level === 'trusted'
            ? `${row.display_name} can build deals and discount up to 20% off standard.`
            : `${row.display_name} can build deals at standard catalog prices.`,
      })
      await load()
    } catch (err: any) {
      toast({
        title: 'Update failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    }
  }

  async function toggleAffiliateStatus(row: any) {
    const next = row.status === 'paused' ? 'active' : 'paused'
    try {
      await api.patch(`/api/affiliates/${row.id}`, { status: next })
      toast({
        title: next === 'paused' ? 'Affiliate suspended' : 'Affiliate resumed',
        description: next === 'paused'
          ? `${row.display_name} can no longer earn commissions or run new demos until resumed.`
          : `${row.display_name} is active again.`,
      })
      await load()
    } catch (err: any) {
      toast({
        title: 'Update failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    }
  }

  async function rejectApplication(row: ApplicationRow) {
    try {
      await api.post(`/api/affiliates/applications/${row.id}/reject`, { reason: 'Rejected from Platform Admin' })
      toast({ title: 'Application rejected' })
      await load()
    } catch (err: any) {
      toast({ title: 'Reject failed', description: err?.response?.data?.message || err?.message, variant: 'destructive' })
    }
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: 'Copied' })
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' })
    }
  }

  async function approveCommission(id: string) {
    try {
      await api.post(`/api/affiliates/commissions/${id}/approve`)
      await load()
    } catch (err: any) {
      toast({ title: 'Approve failed', description: err?.response?.data?.message || err?.message, variant: 'destructive' })
    }
  }

  async function voidCommission(id: string) {
    try {
      await api.post(`/api/affiliates/commissions/${id}/void`, { reason: 'Voided from Platform Admin' })
      await load()
    } catch (err: any) {
      toast({ title: 'Void failed', description: err?.response?.data?.message || err?.message, variant: 'destructive' })
    }
  }

  async function createPayoutBatch() {
    try {
      const res = await api.post('/api/affiliates/payout-batches', {})
      toast({ title: 'Payout batch created', description: money(res.data.batch.total_amount_cents) })
      await load()
    } catch (err: any) {
      toast({ title: 'Batch failed', description: err?.response?.data?.message || err?.message, variant: 'destructive' })
    }
  }

  async function deleteAffiliate() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      const res = await api.delete(`/api/affiliates/${deleteTarget.id}`)
      const d = res.data.deleted || {}
      toast({
        title: 'Affiliate deleted',
        description: `${deleteTarget.display_name} removed${d.sandboxDeleted ? ' (sandbox wiped)' : ''}${d.agentDeleted ? ' (login deleted)' : ''}.`,
      })
      setDeleteTarget(null)
      setDeleteConfirmText('')
      await load()
    } catch (err: any) {
      toast({
        title: 'Delete failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setDeleting(false)
    }
  }

  async function reseedSandbox(row: any) {
    try {
      await api.post(`/api/affiliates/${row.id}/reseed`)
      toast({ title: 'Sandbox re-seeded', description: `${row.display_name}'s demo data refreshed with the latest fixtures.` })
      await load()
    } catch (err: any) {
      toast({
        title: 'Reseed failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    }
  }

  async function provisionPhone(row: any) {
    try {
      const res = await api.post(`/api/affiliates/${row.id}/provision-phone`)
      const d = res.data || {}
      if (d.provisioned) {
        toast({ title: 'Phone provisioned', description: `${row.display_name} now has demo number ${d.phoneNumber}.` })
      } else if (d.reason === 'already_exists') {
        toast({ title: 'Phone already provisioned', description: `Existing demo number: ${d.phoneNumber}` })
      } else {
        toast({
          title: 'Provision failed',
          description: d.reason || 'Telnyx did not return a number. Check API logs.',
          variant: 'destructive',
        })
      }
      await load()
    } catch (err: any) {
      toast({
        title: 'Provision failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    }
  }

  async function sendAccessEmail(row: any) {
    try {
      const res = await api.post(`/api/affiliates/${row.id}/send-access-email`)
      toast({
        title: res.data.sent ? 'Access email sent' : 'Access email generated',
        description: res.data.sent
          ? `${row.display_name} received a fresh password setup link.`
          : 'The email provider did not confirm delivery. Check API logs or Resend configuration.',
      })
    } catch (err: any) {
      toast({
        title: 'Email failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    }
  }

  function openAliasEditor(row: any) {
    const fallbackLocal = String(row.display_name || 'partner')
      .trim()
      .split(/\s+/)[0]
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '')
    setAliasTarget(row)
    setAliasForm({
      localPart: row.email_alias_local_part || fallbackLocal,
      displayName: row.display_name || '',
    })
  }

  async function saveAlias() {
    if (!aliasTarget) return
    setSavingAlias(true)
    try {
      const res = await api.put(`/api/affiliates/${aliasTarget.id}/email-alias`, aliasForm)
      toast({
        title: 'Affiliate alias saved',
        description: res.data.alias?.email_address,
      })
      setAliasTarget(null)
      await load()
    } catch (err: any) {
      toast({
        title: 'Alias failed',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      })
    } finally {
      setSavingAlias(false)
    }
  }

  const pendingApps = applications.filter(app => app.status === 'pending')
  const approvedUnpaid = commissions.filter(row => row.status === 'approved')

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="The Castle"
        title="Affiliates"
        subtitle="Approve partners, manage tracked links, and review commission payouts."
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="card-inset">
          <CardContent className="py-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Pending applications</div>
            <div className="mt-1 text-2xl font-semibold">{pendingApps.length}</div>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardContent className="py-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Active affiliates</div>
            <div className="mt-1 text-2xl font-semibold">{affiliates.filter(a => a.status === 'active').length}</div>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardContent className="py-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Approved unpaid</div>
            <div className="mt-1 text-2xl font-semibold">{money(approvedUnpaid.reduce((s, r) => s + Number(r.commission_amount_cents || 0), 0))}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="applications">
        <TabsList>
          <TabsTrigger value="applications">Applications</TabsTrigger>
          <TabsTrigger value="affiliates">Affiliates</TabsTrigger>
          <TabsTrigger value="commissions">Commissions</TabsTrigger>
        </TabsList>

        <TabsContent value="applications">
          <Card className="card-inset">
            <CardHeader>
              <CardTitle className="text-base">Applications</CardTitle>
              <CardDescription>Approving creates or links an agent, creates a sandbox org, and generates a tracked link.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Applicant</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Audience</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && applications.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">Loading...</TableCell></TableRow>
                    ) : applications.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">No applications yet.</TableCell></TableRow>
                    ) : applications.map(row => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <div className="font-medium">{row.name}</div>
                          <div className="text-xs text-muted-foreground">{row.email}</div>
                        </TableCell>
                        <TableCell>{row.company || '-'}</TableCell>
                        <TableCell className="max-w-sm truncate">{row.audience || row.channel || '-'}</TableCell>
                        <TableCell><Badge variant="outline">{row.status}</Badge></TableCell>
                        <TableCell>{date(row.createdAt)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {row.status === 'pending' && (
                              <>
                                <Button size="sm" onClick={() => setApproveTarget(row)}>
                                  <UserCheck className="mr-1.5 h-4 w-4" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => rejectApplication(row)}>
                                  <X className="mr-1.5 h-4 w-4" /> Reject
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => setDeleteAppTarget(row)}
                              title="Delete application (clears the email from this list)"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="affiliates">
          <Card className="card-inset">
            <CardHeader>
              <CardTitle className="text-base">Approved affiliates</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                      <TableRow>
                        <TableHead>Affiliate</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Terms</TableHead>
                        <TableHead>Referrals</TableHead>
                        <TableHead>Link</TableHead>
                        <TableHead>Access</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                      </TableRow>
                  </TableHeader>
                  <TableBody>
                    {affiliates.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No affiliates yet.</TableCell></TableRow>
                    ) : affiliates.map(row => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <div className="font-medium">{row.display_name}</div>
                          <div className="text-xs text-muted-foreground">{row.email}</div>
                          {row.email_alias && (
                            <div className="mt-0.5 font-mono text-xs text-muted-foreground">{row.email_alias}</div>
                          )}
                        </TableCell>
                        <TableCell><Badge variant="outline">{row.status}</Badge></TableCell>
                        <TableCell>
                          <div className="space-y-1.5">
                            <div className="text-sm">
                              {Number(row.default_commission_pct)}% · {row.commission_unlimited ? 'Unlimited' : `${row.commission_term_months} mo`}
                            </div>
                            <Select
                              value={row.deal_builder_access || 'none'}
                              onValueChange={(v) => setDealBuilderAccess(row, v)}
                            >
                              <SelectTrigger
                                className="h-7 w-[156px] text-xs"
                                title="Deal Builder access for this affiliate"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Deals: No access</SelectItem>
                                <SelectItem value="builder">Deals: Builder</SelectItem>
                                <SelectItem value="trusted">Deals: Trusted (−20%)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                        <TableCell>{row.referrals || 0}</TableCell>
                        <TableCell>
                          {row.primaryUrl ? (
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={() => copy(row.primaryUrl)}>
                                <Copy className="h-4 w-4" />
                              </Button>
                              <a className="text-sm underline" href={row.primaryUrl} target="_blank" rel="noreferrer">
                                <ExternalLink className="inline h-3.5 w-3.5" />
                              </a>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              asChild
                              title={`Preview ${row.display_name}'s affiliate dashboard (read-only)`}
                            >
                              <Link to={`/affiliate?as=${encodeURIComponent(row.id)}`}>
                                <Eye className="mr-1.5 h-4 w-4" /> Preview
                              </Link>
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openAliasEditor(row)} title="Edit affiliate email alias">
                              <AtSign className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => sendAccessEmail(row)}>
                              <Mail className="mr-1.5 h-4 w-4" /> Send access
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => provisionPhone(row)}
                              title="Provision a Telnyx demo phone number for this affiliate's sandbox"
                            >
                              <Phone className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => reseedSandbox(row)}
                              title="Wipe + re-seed sample data (leads, calls, chats, bookings) — keeps phone & wizard agents"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => toggleAffiliateStatus(row)}
                              title={row.status === 'paused' ? 'Resume affiliate' : 'Suspend affiliate (no commissions, no demos)'}
                            >
                              {row.status === 'paused'
                                ? <Play className="h-4 w-4 text-emerald-600" />
                                : <Pause className="h-4 w-4 text-amber-600" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => { setDeleteTarget(row); setDeleteConfirmText('') }}
                              title="Delete affiliate (wipes sandbox + login)"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{money(row.paid_cents)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="commissions">
          <Card className="card-inset">
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">Commission ledger</CardTitle>
                <CardDescription>Approve entries before creating a manual payout batch.</CardDescription>
              </div>
              <Button onClick={createPayoutBatch} disabled={approvedUnpaid.length === 0}>
                <WalletCards className="mr-2 h-4 w-4" /> Batch approved
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Affiliate</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Paid at</TableHead>
                      <TableHead className="text-right">Commission</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissions.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">No commission entries yet.</TableCell></TableRow>
                    ) : commissions.map(row => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <div className="font-medium">{row.affiliate_name}</div>
                          <div className="text-xs text-muted-foreground">{row.affiliate_email}</div>
                        </TableCell>
                        <TableCell>{row.organization_name}</TableCell>
                        <TableCell><Badge variant="outline">{row.status}</Badge></TableCell>
                        <TableCell>{date(row.invoice_paid_at)}</TableCell>
                        <TableCell className="text-right">{money(row.commission_amount_cents)}</TableCell>
                        <TableCell className="text-right">
                          {row.status === 'pending' && (
                            <div className="flex justify-end gap-2">
                              <Button size="sm" onClick={() => approveCommission(row.id)}>
                                <Check className="mr-1.5 h-4 w-4" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => voidCommission(row.id)}>Void</Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!approveTarget} onOpenChange={(open) => !open && setApproveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve affiliate</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <div className="font-medium">{approveTarget?.name}</div>
              <div className="text-sm text-muted-foreground">{approveTarget?.email}</div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="commissionPct">Commission %</Label>
                <Input
                  id="commissionPct"
                  type="number"
                  min={0}
                  max={75}
                  value={approveForm.commissionPct}
                  onChange={e => setApproveForm(prev => ({ ...prev, commissionPct: Number(e.target.value) }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="termMonths">Term months</Label>
                <Input
                  id="termMonths"
                  type="number"
                  min={1}
                  max={120}
                  disabled={approveForm.unlimited}
                  value={approveForm.termMonths}
                  onChange={e => setApproveForm(prev => ({ ...prev, termMonths: Number(e.target.value) }))}
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label htmlFor="unlimited">Unlimited recurring commission</Label>
                <p className="text-xs text-muted-foreground">Overrides the term cap for this affiliate.</p>
              </div>
              <Switch
                id="unlimited"
                checked={approveForm.unlimited}
                onCheckedChange={checked => setApproveForm(prev => ({ ...prev, unlimited: checked }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="internalNotes">Internal notes</Label>
              <Textarea
                id="internalNotes"
                value={approveForm.internalNotes}
                onChange={e => setApproveForm(prev => ({ ...prev, internalNotes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)} disabled={approving}>Cancel</Button>
            <Button onClick={approveApplication} disabled={approving}>
              {approving ? 'Approving…' : 'Approve affiliate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteAppTarget} onOpenChange={(open) => { if (!open) setDeleteAppTarget(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete application?</DialogTitle>
            <DialogDescription>
              Permanently removes <span className="font-medium text-foreground">{deleteAppTarget?.email}</span> from the applications list. They can re-apply afterwards. This does not affect any approved affiliate accounts.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAppTarget(null)} disabled={deletingApp}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDeleteApplication} disabled={deletingApp}>
              {deletingApp ? 'Deleting…' : 'Delete application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!aliasTarget} onOpenChange={(open) => { if (!open) setAliasTarget(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit affiliate email alias</DialogTitle>
            <DialogDescription>
              This controls the address used by the affiliate inbox/outbox.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="affiliateEmailDisplayName">Display name</Label>
              <Input
                id="affiliateEmailDisplayName"
                value={aliasForm.displayName}
                onChange={e => setAliasForm(prev => ({ ...prev, displayName: e.target.value }))}
                placeholder={aliasTarget?.display_name || 'LegalLady Partner'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="affiliateEmailLocalPart">Email alias</Label>
              <div className="flex rounded-md border bg-background">
                <Input
                  id="affiliateEmailLocalPart"
                  value={aliasForm.localPart}
                  onChange={e => setAliasForm(prev => ({ ...prev, localPart: e.target.value }))}
                  className="border-0 focus-visible:ring-0"
                  placeholder="tom"
                />
                <div className="flex items-center border-l px-3 font-mono text-xs text-muted-foreground">
                  @{aliasTarget?.email_alias?.split('@')[1] || 'partners.legallady.ai'}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAliasTarget(null)} disabled={savingAlias}>Cancel</Button>
            <Button onClick={saveAlias} disabled={savingAlias || !aliasForm.localPart.trim()}>
              {savingAlias ? 'Saving…' : 'Save alias'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirmText('') } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete affiliate</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <p className="font-medium text-destructive">This permanently:</p>
              <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground space-y-0.5">
                <li>Wipes their demo sandbox (sample leads, voice assistants, calls, chats)</li>
                <li>Releases any Telnyx phone number on the sandbox</li>
                <li>Deletes commission ledger, referrals, links, application</li>
                <li>Deletes the login if they have no other org memberships</li>
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">
                Referred firms (real customers) are <strong>not</strong> deleted — only the affiliate's attribution is removed.
              </p>
            </div>
            <div>
              <div className="font-medium">{deleteTarget?.display_name}</div>
              <div className="text-sm text-muted-foreground">{deleteTarget?.email}</div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmDelete">
                Type <span className="font-mono font-semibold">{deleteTarget?.email}</span> to confirm
              </Label>
              <Input
                id="confirmDelete"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder={deleteTarget?.email}
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirmText('') }}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={deleteAffiliate}
              disabled={deleting || deleteConfirmText.trim().toLowerCase() !== (deleteTarget?.email || '').toLowerCase()}
            >
              {deleting ? 'Deleting...' : 'Delete affiliate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
